// Single source of truth for editable core-page content: default content (used as
// fallback by the public pages) + the admin editor field definitions.

export const PAGE_SCHEMAS = {
  "/about-us": {
    name: "About Us",
    defaults: {
      eyebrow: "About CoworkZen",
      title: "Workspaces built for what's next",
      image: "",
      paragraphs: [
        "CoworkZen designs and operates premium, fully managed coworking and office spaces across Noida's most connected business districts. From freelancers and fast-growing startups to established enterprises, we provide flexible, future-ready workspaces backed by vibrant communities, modern amenities and dependable on-site support.",
        "Our mission is simple: create good spaces for great people. Every CoworkZen centre blends high-speed connectivity, thoughtful design and hospitality-grade service so your team can focus on doing their best work.",
      ],
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "image", label: "Hero Image (optional)", type: "image" },
      { key: "paragraphs", label: "Body Paragraphs", type: "string-list", itemLabel: "Paragraph", textarea: true },
    ],
  },

  "/amenities": {
    name: "Amenities",
    defaults: {
      eyebrow: "Amenities",
      title: "Everything your team needs to do great work",
      subtitle: "Every CoworkZen centre comes fully loaded with modern amenities and hospitality-grade service.",
      image: "",
      items: [
        { icon: "wifi", title: "High-Speed Internet", desc: "Enterprise-grade fibre with redundant backup lines." },
        { icon: "video", title: "Meeting & Video Rooms", desc: "Bookable rooms with 4K displays and conferencing." },
        { icon: "coffee", title: "Café & Pantry", desc: "Barista-style coffee, tea and refreshments all day." },
        { icon: "zap", title: "24x7 Power Backup", desc: "Uninterrupted power so you never lose momentum." },
        { icon: "shield-check", title: "Security & Access", desc: "CCTV, manned reception and smart access control." },
        { icon: "printer", title: "Print & Scan", desc: "On-site printing, scanning and admin support." },
        { icon: "users", title: "Vibrant Community", desc: "Networking, member events and community mixers." },
        { icon: "presentation", title: "Event & Training Space", desc: "Flexible spaces for workshops and town halls." },
      ],
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "subtitle", label: "Subheading", type: "textarea" },
      { key: "image", label: "Hero Image (optional)", type: "image" },
      {
        key: "items", label: "Amenity Cards", type: "cards", itemLabel: "Amenity",
        item: [
          { key: "icon", label: "Icon (lucide name, e.g. wifi)", type: "text" },
          { key: "title", label: "Title", type: "text" },
          { key: "desc", label: "Description", type: "textarea" },
        ],
      },
    ],
  },

  "/pricing": {
    name: "Pricing",
    defaults: {
      eyebrow: "Pricing & Plans",
      title: "Simple, flexible pricing for every team",
      subtitle: "From a single day pass to fully managed private offices — pick what fits and scale anytime.",
      image: "",
      footnote: "Prices are indicative and vary by location.",
      plans: [
        { name: "Day Pass", price: "₹499", unit: "/ day", features: ["Hot desk for a full day", "High-speed Wi-Fi", "Café & pantry access", "Community areas"], featured: false, href: "https://app.coworkzen.com/day-pass/46/59" },
        { name: "Dedicated Desk", price: "₹8,999", unit: "/ month", features: ["Your own reserved desk", "24x7 access", "Meeting room credits", "Business address"], featured: true, href: "" },
        { name: "Private Office", price: "Custom", unit: "", features: ["Lockable private cabin", "Team of any size", "Priority meeting rooms", "Dedicated support"], featured: false, href: "" },
      ],
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "subtitle", label: "Subheading", type: "textarea" },
      { key: "image", label: "Hero Image (optional)", type: "image" },
      {
        key: "plans", label: "Pricing Plans", type: "cards", itemLabel: "Plan",
        item: [
          { key: "name", label: "Plan Name", type: "text" },
          { key: "price", label: "Price", type: "text" },
          { key: "unit", label: "Unit (e.g. / month)", type: "text" },
          { key: "features", label: "Features", type: "string-list", itemLabel: "Feature" },
          { key: "featured", label: "Highlight as 'Most Popular'", type: "bool" },
          { key: "href", label: "External CTA link (optional)", type: "text" },
        ],
      },
      { key: "footnote", label: "Footnote", type: "text" },
    ],
  },

  "/coworking-space-in-noida-for-a-day": {
    name: "Day Pass",
    defaults: {
      eyebrow: "Day Pass",
      title: "Coworking space in Noida for a day",
      subtitle: "Need a professional space just for today? Grab a day pass and get a productive desk, fast Wi-Fi and café access — no commitment.",
      image: "",
      items: [
        { icon: "calendar-clock", title: "Flexible, daily", desc: "Come in for a single day — extend anytime you need." },
        { icon: "wifi", title: "Plug in & go", desc: "High-speed Wi-Fi, power and a comfortable desk ready for you." },
        { icon: "coffee", title: "Perks included", desc: "Café, pantry and community areas included with every pass." },
      ],
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "subtitle", label: "Subheading", type: "textarea" },
      { key: "image", label: "Hero Image (optional)", type: "image" },
      {
        key: "items", label: "Highlight Cards", type: "cards", itemLabel: "Highlight",
        item: [
          { key: "icon", label: "Icon (lucide name)", type: "text" },
          { key: "title", label: "Title", type: "text" },
          { key: "desc", label: "Description", type: "textarea" },
        ],
      },
    ],
  },

  "/conference-room-in-noida": {
    name: "Conference Rooms",
    defaults: {
      eyebrow: "Meeting & Conference Rooms",
      title: "Conference rooms in Noida, by the hour or day",
      subtitle: "Impress clients and run productive sessions in fully equipped meeting and conference rooms across our Noida centres.",
      image: "",
      items: [
        { icon: "video", title: "4K Video Conferencing" },
        { icon: "presentation", title: "Displays & Whiteboards" },
        { icon: "wifi", title: "High-Speed Wi-Fi" },
        { icon: "users", title: "2–20 Seater Rooms" },
      ],
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "subtitle", label: "Subheading", type: "textarea" },
      { key: "image", label: "Hero Image (optional)", type: "image" },
      {
        key: "items", label: "Feature Chips", type: "cards", itemLabel: "Feature",
        item: [
          { key: "icon", label: "Icon (lucide name)", type: "text" },
          { key: "title", label: "Label", type: "text" },
        ],
      },
    ],
  },

  "/404": {
    name: "404 / Not Found",
    defaults: {
      eyebrow: "We looked everywhere",
      title: "This page took a day off",
      subtitle: "The page you're after may have moved or never existed — but your perfect workspace is only a click away. Here's where to go next.",
      image: "",
      links: [
        { icon: "home", title: "Homepage", desc: "Start fresh from our homepage.", href: "/" },
        { icon: "building-2", title: "Office Space", desc: "Explore flexible desks, cabins and private offices.", href: "/office-space" },
        { icon: "map-pin", title: "Locations", desc: "Find a CoworkZen centre near you.", href: "/locations" },
        { icon: "phone", title: "Contact Us", desc: "Talk to our team and we'll help you out.", href: "/contact" },
      ],
      cta_title: "Looking for the right space?",
      cta_subtitle: "Book a guided tour and we'll match you to the perfect workspace in minutes.",
      cta_label: "Book a Tour",
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "subtitle", label: "Subheading", type: "textarea" },
      { key: "image", label: "Hero Image (optional)", type: "image" },
      {
        key: "links", label: "Helpful Links", type: "cards", itemLabel: "Link",
        item: [
          { key: "icon", label: "Icon (lucide name, e.g. home)", type: "text" },
          { key: "title", label: "Title", type: "text" },
          { key: "desc", label: "Description", type: "textarea" },
          { key: "href", label: "Link (e.g. /office-space)", type: "text" },
        ],
      },
      { key: "cta_title", label: "CTA Title", type: "text" },
      { key: "cta_subtitle", label: "CTA Subtitle", type: "textarea" },
      { key: "cta_label", label: "CTA Button Label", type: "text" },
    ],
  },

  "/contact": {
    name: "Contact",
    defaults: {
      eyebrow: "Contact",
      title: "Let's talk workspace",
      subtitle: "Tell us what you need and our team will help you find the perfect space.",
      cta_title: "Ready to get started?",
      cta_subtitle: "Book a guided tour or request a customised quote in under a minute.",
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "title", label: "Heading", type: "text" },
      { key: "subtitle", label: "Subheading", type: "textarea" },
      { key: "cta_title", label: "CTA Card Title", type: "text" },
      { key: "cta_subtitle", label: "CTA Card Subtitle", type: "textarea" },
    ],
  },
};

// Deep-ish merge: stored content overrides defaults per top-level key (arrays kept whole).
export function withDefaults(path, content) {
  const d = PAGE_SCHEMAS[path]?.defaults || {};
  return { ...structuredClone(d), ...(content || {}) };
}
